import { Fragment, useEffect, useState } from 'react'
import { Formik } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { useMutation, gql, useLazyQuery } from '@apollo/client'
import styled from 'styled-components'
import { isEmpty, isFinite, isNumber } from 'lodash'

import { Loader } from '@10xLabs/react-web/components/Loader'
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from 'components/v2/Table'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import FormLayoutV2 from 'components/v2/FormLayout'
import FormLayout from 'features/App/components/FormLayout'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { getUnprocessableErrors } from 'utils/error'
import { canWrite } from 'features/Users/utils'
import { round } from 'utils/math'
import { getDifference } from 'utils/object'
import { schema } from './schema'
import { getAllSegmentsByRoute, isSegmentCreationCompleted } from 'features/Segments/hooks'
import { InputDuration } from 'components/InputDuration'
import { InputDistance } from 'components/InputDistance'
import { GTCompanyV2Vertical, GTSegment } from 'gql/types.generated'
import { useLogin } from 'features/Login/hooks'

const TitleContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`

const StyledTd = styled(Td)<{ isMiddle?: boolean }>`
  padding: ${({ isMiddle }): string => (isMiddle ? '20px 10px' : '10px')};
`

const messages = defineMessages({
  error: {
    id: 'dictionary.error',
    defaultMessage: 'There was an error while editing your segments. Please try again.'
  }
})

const Form = (props: TSFixMe): TSFixMe => {
  const {
    values,
    touched,
    errors,
    setFieldValue,
    setFieldTouched,
    error,
    loading,
    handleSubmit,
    onCancel,
    login,
    initialSegments,
    submitLabel,
    route,
    onSkip,
    oldSegments
  } = props
  const intl = useIntl()

  const canSkipSegmentsForm = route.company?.vertical === GTCompanyV2Vertical.Team

  const [editSegment, { loading: editSegmentLoading }] = useMutation(EDIT_SEGMENT)
  const [addCompanyLineSpec, { loading: addCompanyLineSpecsLoading }] = useMutation(ADD_SEGMENT_COMPANY_LINE_SPEC)

  const handleSkip = async (): Promise<void> => {
    for (const [segmentIndex, segment] of values.segments.entries()) {
      for (const [companyLineSpecIndex, companyLineSpec] of segment.companyLineSpecs.entries()) {
        const oldCompanyLineSpec = initialSegments[segmentIndex].companyLineSpecs[companyLineSpecIndex]
        const oldDistance = isNumber(oldCompanyLineSpec.distance)
          ? round(parseFloat(oldCompanyLineSpec.distance) * 1000)
          : null
        const oldDuration = isNumber(oldCompanyLineSpec.duration) ? parseInt(oldCompanyLineSpec.duration) : null
        const hasNullSpec = oldDuration === null || oldDistance === null

        if (hasNullSpec) {
          const newCompanyLineSpec = {
            companyLineID: companyLineSpec.companyLine.id,
            duration: oldDuration || 0,
            distance: oldDistance || 0
          }

          const existingCompanyLineSpec = oldSegments[segmentIndex].companyLineSpecs.find(
            ({ companyLine }: TSFixMe) => companyLine.id === companyLineSpec.companyLine.id
          )

          if (!existingCompanyLineSpec) {
            await addCompanyLineSpec({
              variables: {
                segmentId: segment.id,
                companyLineSpec: newCompanyLineSpec
              }
            })
          } else {
            await editSegment({
              variables: {
                segmentId: segment.id,
                companyLineSpecs: [newCompanyLineSpec]
              }
            })
          }
        }
      }
    }
    onSkip?.()
  }

  return (
    <FormLayoutV2.Content as="form" autoComplete="off" onSubmit={handleSubmit}>
      <FormLayoutV2.Card>
        <TitleContainer>
          <Heading.H5>
            <FormattedMessage id="changeRoute.summarySegments.title" defaultMessage="Summary segments" />
          </Heading.H5>
          {canSkipSegmentsForm && (
            <Button
              type="button"
              kind="secondary"
              onClick={handleSkip}
              loading={addCompanyLineSpecsLoading || editSegmentLoading}
            >
              <FormattedMessage id="changeRoute.summarySegments.skip" defaultMessage="Skip" />
            </Button>
          )}
        </TitleContainer>
        <>
          {initialSegments[0].companyLineSpecs.map(
            (companyLineSpec: TSFixMe, companyLineSpecIndex: TSFixMe): TSFixMe => {
              return (
                <Fragment key={companyLineSpecIndex}>
                  <Heading.Body bold>
                    <FormattedMessage
                      id="segments.form.companyLine.title"
                      defaultMessage="Line {name}"
                      values={{
                        name: companyLineSpec.companyLine.name
                      }}
                    />
                  </Heading.Body>
                  <TableContainer overflow>
                    <Table>
                      <Thead>
                        <Tr>
                          <Th noPaddingTop>
                            <FormattedMessage id="dictionary.segment" defaultMessage="Segment" />
                          </Th>
                          <Th noPaddingTop>
                            <FormattedMessage id="dictionary.duration" defaultMessage="Duration" />
                          </Th>
                          <Th noPaddingTop>
                            <FormattedMessage id="dictionary.distance" defaultMessage="Distance" />
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {values.segments.map((segment: TSFixMe, segmentIndex: TSFixMe): TSFixMe => {
                          const oldDuration =
                            initialSegments[segmentIndex].companyLineSpecs[companyLineSpecIndex].duration
                          const durationHours =
                            values.segments[segmentIndex].companyLineSpecs[companyLineSpecIndex].durationHours
                          const durationMinutes =
                            values.segments[segmentIndex].companyLineSpecs[companyLineSpecIndex].durationMinutes
                          const durationHoursKey = `segments[${segmentIndex}].companyLineSpecs[${companyLineSpecIndex}].durationHours`
                          const durationMinutesKey = `segments[${segmentIndex}].companyLineSpecs[${companyLineSpecIndex}].durationMinutes`
                          const durationError =
                            errors.segments?.[segmentIndex]?.companyLineSpecs?.[companyLineSpecIndex]?.durationHours ||
                            errors.segments?.[segmentIndex]?.companyLineSpecs?.[companyLineSpecIndex]?.durationMinutes
                          const durationTouched =
                            touched.segments?.[segmentIndex]?.companyLineSpecs?.[companyLineSpecIndex]?.durationHours ||
                            touched.segments?.[segmentIndex]?.companyLineSpecs?.[companyLineSpecIndex]?.durationMinutes
                          const oldDistance =
                            initialSegments[segmentIndex].companyLineSpecs[companyLineSpecIndex].distance
                          const newDistance =
                            values.segments[segmentIndex].companyLineSpecs[companyLineSpecIndex].distance
                          const distanceKey = `segments[${segmentIndex}].companyLineSpecs[${companyLineSpecIndex}].distance`
                          return (
                            <Tr key={segmentIndex}>
                              <StyledTd isMiddle>
                                {segment.origin.code} - {segment.destination.code}
                              </StyledTd>
                              <StyledTd>
                                {canWrite(FEATURES.SEGMENT, login.accessToken.policies) ? (
                                  <InputDuration
                                    hours={durationHours}
                                    setHours={(value: number | ''): void => setFieldValue(durationHoursKey, value)}
                                    minutes={durationMinutes}
                                    setMinutes={(value: number | ''): void => setFieldValue(durationMinutesKey, value)}
                                    onBlur={(): void => {
                                      setFieldTouched(durationHoursKey, true)
                                      setFieldTouched(durationMinutesKey, true)
                                    }}
                                    touched={durationTouched}
                                    errors={durationError}
                                    disabled={isNumber(oldDuration)}
                                  />
                                ) : (
                                  '-'
                                )}
                              </StyledTd>
                              <StyledTd>
                                {canWrite(FEATURES.SEGMENT, login.accessToken.policies) ? (
                                  <InputDistance
                                    distance={newDistance}
                                    setDistance={(value: number | ''): void => setFieldValue(distanceKey, value)}
                                    onBlur={(): void => {
                                      setFieldTouched(distanceKey, true)
                                    }}
                                    touched={
                                      touched.segments?.[segmentIndex]?.companyLineSpecs?.[companyLineSpecIndex]
                                        ?.distance
                                    }
                                    errors={
                                      errors.segments?.[segmentIndex]?.companyLineSpecs?.[companyLineSpecIndex]
                                        ?.distance
                                    }
                                    disabled={isNumber(oldDistance)}
                                  />
                                ) : (
                                  '-'
                                )}
                              </StyledTd>
                            </Tr>
                          )
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Fragment>
              )
            }
          )}
        </>
      </FormLayoutV2.Card>
      {error && (
        <FormLayoutV2.Actions>
          <ErrorMessage errors={getUnprocessableErrors(error)}>{intl.formatMessage(messages.error)}</ErrorMessage>
        </FormLayoutV2.Actions>
      )}
      <FormLayoutV2.Actions>
        <Button kind="cancel" onClick={onCancel}>
          <FormattedMessage id="dictionary.previous" defaultMessage="Previous" />
        </Button>

        <Button type="submit" kind="primary" loading={loading}>
          {submitLabel || <FormattedMessage id="dictionary.create" defaultMessage="Create" />}
        </Button>
      </FormLayoutV2.Actions>
    </FormLayoutV2.Content>
  )
}

const GET_SEGMENTS = gql`
  query GetSegments($routeId: ID) {
    segments(input: { limit: 1000, routeID: $routeId }) {
      id
      origin {
        name
        code
        id
      }
      destination {
        name
        code
        id
      }
      companyLineSpecs {
        companyLine {
          id
          name
        }
        duration
        distance
      }
    }
  }
`

const EDIT_SEGMENT = gql`
  mutation EditSegment($segmentId: ID!, $companyLineSpecs: [companyLineSpecInput!]!) {
    updateSegment(input: { id: $segmentId, companyLineSpecs: $companyLineSpecs })
  }
`

const ADD_SEGMENT_COMPANY_LINE_SPEC = gql`
  mutation AddSegmentCompanyLineSpec($segmentId: ID!, $companyLineSpec: addCompanyLineSpecInput!) {
    addSegmentCompanyLineSpec(input: { id: $segmentId, companyLineSpec: $companyLineSpec })
  }
`

export const SummaryForm = ({
  route,
  lines = [],
  onSubmit,
  onCancel,
  submitLabel,
  step,
  scrollToTop
}: TSFixMe): TSFixMe => {
  const intl = useIntl()

  const [oldSegments, setOldSegments] = useState<GTSegment[]>([])
  const login = useLogin()
  const [getSegmentsQuery] = useLazyQuery<{ segments: GTSegment[] }, { routeId: string }>(GET_SEGMENTS, {
    fetchPolicy: 'network-only'
  })
  const [editSegment, { loading: editSegmentLoading, error: editSegmentError }] = useMutation(EDIT_SEGMENT)
  const [addCompanyLineSpec, { loading: addCompanyLineSpecLoading, error: addCompanyLineSpecError }] =
    useMutation(ADD_SEGMENT_COMPANY_LINE_SPEC)
  const getSegments = async (): Promise<void> => {
    const segments = await getAllSegmentsByRoute(route, getSegmentsQuery)
    setOldSegments(segments)
  }
  useEffect(() => {
    getSegments()
  }, [])
  useEffect(() => {
    scrollToTop?.()
  }, [scrollToTop])

  const isLoading = !isSegmentCreationCompleted(route, oldSegments)
  if (isLoading) {
    return (
      <FormLayout.CardContent>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </FormLayout.CardContent>
    )
  } else {
    const initialSegments =
      oldSegments.map((segment: TSFixMe): TSFixMe => {
        const companyLineSpecs = lines.map((line: TSFixMe): TSFixMe => {
          let duration = ''
          let distance = ''

          const companyLineSpec = segment.companyLineSpecs.find(
            ({ companyLine }: TSFixMe): TSFixMe => companyLine.id === line.id
          )

          if (companyLineSpec) {
            duration = isNumber(companyLineSpec.duration) ? companyLineSpec.duration : ''
            // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
            distance = isFinite(companyLineSpec.distance) ? round(companyLineSpec.distance / 1000) : ''
          }

          return {
            companyLine: {
              id: line.id,
              name: line.name
            },
            durationHours: isNumber(duration) ? Math.floor(duration / 60) : '',
            durationMinutes: isNumber(duration) ? duration % 60 : '',
            duration,
            distance
          }
        })

        return {
          ...segment,
          companyLineSpecs
        }
      }) || []

    const handleSubmit = async ({ segments }: TSFixMe): Promise<void> => {
      for (const [segmentIndex, segment] of segments.entries()) {
        for (const [companyLineSpecIndex, companyLineSpec] of segment.companyLineSpecs.entries()) {
          const distance = round(parseFloat(companyLineSpec.distance) * 1000)
          const duration = companyLineSpec.durationHours * 60 + companyLineSpec.durationMinutes
          const oldCompanyLineSpec = initialSegments[segmentIndex].companyLineSpecs[companyLineSpecIndex]
          const oldDistance = isNumber(oldCompanyLineSpec.distance)
            ? round(parseFloat(oldCompanyLineSpec.distance) * 1000)
            : null
          const oldDuration = isNumber(oldCompanyLineSpec.duration) ? parseInt(oldCompanyLineSpec.duration) : null
          const payload = getDifference(
            {
              distance,
              duration
            },
            {
              distance: oldDistance,
              duration: oldDuration
            }
          )

          if (!isEmpty(payload)) {
            const existingCompanyLineSpec = oldSegments[segmentIndex].companyLineSpecs.find(
              ({ companyLine }: TSFixMe): TSFixMe => companyLine.id === companyLineSpec.companyLine.id
            )

            if (!existingCompanyLineSpec) {
              await addCompanyLineSpec({
                variables: {
                  segmentId: segment.id,
                  companyLineSpec: {
                    companyLineID: companyLineSpec.companyLine.id,
                    distance,
                    duration
                  }
                }
              })
            } else {
              await editSegment({
                variables: {
                  segmentId: segment.id,
                  companyLineSpecs: [
                    {
                      companyLineID: existingCompanyLineSpec.companyLine.id,
                      distance,
                      duration
                    }
                  ]
                }
              })
            }
          }
        }
      }
      onSubmit()
    }

    const handleSkip = (): void => {
      onSubmit()
    }

    const error = editSegmentError || addCompanyLineSpecError
    const loading = editSegmentLoading || addCompanyLineSpecLoading

    return (
      <Formik
        initialValues={{
          segments: initialSegments
        }}
        // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
        validationSchema={schema(intl, canWrite(FEATURES.SEGMENT, login.accessToken.policies))}
        onSubmit={handleSubmit}
      >
        {(props): TSFixMe => {
          return (
            <Form
              {...props}
              initialSegments={initialSegments}
              onCancel={onCancel}
              error={error}
              submitLabel={submitLabel}
              loading={loading}
              route={route}
              login={login}
              step={step}
              oldSegments={oldSegments}
              onSkip={handleSkip}
            />
          )
        }}
      </Formik>
    )
  }
}
